import { Stack, Typography, Button, Theme } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { ChangeEvent } from 'react';
import { validationForgotPasswordSchema } from './validationSchema';
import { StyledForm } from '../../common/StyledForm/StyledForm';
import { CustomInput } from '../../common/CustomInput/CustomInput';
import { authService } from '../../../services/auth/AuthService';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { useUser } from '../../../hooks/useUser';
import { useResendCooldown } from '../../../hooks/useResendCooldown';
import { useFormErrorHandler } from '../../../hooks/useFormErrorHandler';

export const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();

  const { countdown, isCoolingDown, start: startCooldown } =
    useResendCooldown();
  const { serverError, setServerError, handleApiError } = useFormErrorHandler({
    conflictError: t('userAuth.recoverPassword.validationErrors.matchPasswords'),
    validationError: t('errors.invalidEmailConfirmationCode'),
  });

  const {
    values,
    errors,
    handleSubmit,
    touched,
    handleChange,
    setFieldTouched,
    isSubmitting,
    setFieldError,
  } = useFormik({
    initialValues: {
      code: '',
      password: '',
      repeatPassword: '',
    },
    validationSchema: validationForgotPasswordSchema(t),
    initialTouched: {
      code: false,
      password: false,
      repeatPassword: false,
    },
    onSubmit: async ({ code, password, repeatPassword }) => {
      let field: 'phone' | 'email' | null = null;
      if (user?.phone) field = 'phone';
      if (user?.email) field = 'email';

      if (!field) return;

      try {
        await authService.sendResetPasswordCompareCode({
          [field]: user?.[field] as string,
          code,
          password,
          repeatPassword,
        });
        navigate(`/${ROUTES.LOGIN}`);
      } catch (error) {
        const handled = handleApiError(error);
        if ('code' in handled) {
          setFieldError('code', (handled as { code?: string }).code ?? '');
        }
      }
    },
    enableReinitialize: true,
  });

  const resendCode = async () => {
    setServerError(null);
    startCooldown();
    if (!user?.phone) return;
    try {
      await authService.resendResetPasswordCode(user?.phone);
    } catch (error) {
      handleApiError(error);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(e);
    setServerError(null);
  };

  const checkDisableButton = () => {
    const isEmptyFields = !!values.password || !!values.repeatPassword;
    return !isEmptyFields || !!Object.keys(errors).length || isSubmitting;
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <Stack
        direction="column"
        sx={{
          marginTop: '30px',
          gap: '15px',
        }}
      >
        <Typography color="common.white">
          {t('userAuth.phoneConfirmation.textPart1')}
          {t('userAuth.phoneConfirmation.textPart2')}
          <br />
          <br />
          {t('userAuth.phoneConfirmation.textPart3')}
        </Typography>
        <CustomInput
          fieldName={'code'}
          // The code arrives by SMS, so iOS can offer it straight from the
          // notification — but only for a field that says it is a one-time
          // code and takes digits. It was getting a qwerty keyboard and no
          // offer at all.
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          label={t('userAuth.phoneConfirmation.inputPlaceholder')}
          value={values.code}
          onChange={onChange}
          error={serverError || (touched['code'] && errors.code)}
        />
        <Typography color="common.white">
          {t('profilePage.updatePasswordForm.formTitle')}
        </Typography>
        <CustomInput
          fieldName={'password'}
          autoComplete="new-password"
          label={t('userAuth.passwordPlaceholder')}
          isPassword={true}
          value={values.password}
          onChange={(e) => {
            setFieldTouched('password');
            handleChange(e);
          }}
          error={touched['password'] && errors.password}
        />
        <CustomInput
          fieldName={'repeatPassword'}
          autoComplete="new-password"
          label={t('userAuth.repeatPasswordPlaceholder')}
          isPassword={true}
          value={values.repeatPassword}
          onChange={(e) => {
            setFieldTouched('repeatPassword');
            handleChange(e);
          }}
          error={touched['repeatPassword'] && errors.repeatPassword}
        />
        <Stack direction="row" justifyContent="space-between" gap={1}>
          <Button
            variant="text"
            color="primary"
            onClick={resendCode}
            sx={(theme: Theme) => ({
              padding: '0 10px',
              '&.MuiButtonBase-root.Mui-disabled': {
                color: theme.palette.gray[50],
              },
              width: 'max-content',
            })}
            disabled={isCoolingDown}
          >
            {countdown ? (
              <>
                {t('userAuth.phoneConfirmation.resentCodeText')}
                <br />({countdown} {t('userAuth.phoneConfirmation.sec')})
              </>
            ) : (
              t('userAuth.phoneConfirmation.resentCodeText')
            )}
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
            disabled={checkDisableButton()}
          >
            {t('userAuth.phoneConfirmation.btnText')}
          </LoadingButton>
        </Stack>
      </Stack>
    </StyledForm>
  );
};
